import React from "react";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

export type DashboardCourseCardProps = {
  href: string;
  title: string;
  author?: string | null;
  coverImageUrl?: string | null;
  coverUrl?: string | null;
  iconUrl?: string | null;
  iconEmoji?: string | null;
  accentColor?: string | null;
  showProgress?: boolean;
  progressPct?: number;
  actions?: React.ReactNode;
};

export const CourseCard: React.FC<DashboardCourseCardProps> = ({
  href,
  title,
  author,
  coverImageUrl,
  coverUrl,
  iconUrl,
  iconEmoji,
  accentColor,
  showProgress = false,
  progressPct = 0,
  actions,
}) => {
  const coverBackground = coverImageUrl || coverUrl
    ? `url("${coverImageUrl || coverUrl}")`
    : "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.6) 100%)";

  return (
    <div className="group/course-card relative h-full">
      {actions ? (
        <div className="absolute right-3 top-3 z-20 opacity-0 transition-opacity duration-200 group-hover/course-card:opacity-100 group-focus-within/course-card:opacity-100">
          {actions}
        </div>
      ) : null}

      <a href={href} className="block h-full group">
        <div className="h-full overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-all duration-200 hover:border-primary/20 hover:shadow-md">
        <div
          className="relative flex h-14 items-center justify-end px-4"
          style={{
            backgroundImage: coverBackground,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

          <div className="absolute bottom-0 left-4 z-10 translate-y-1/2">
            <div className="rounded-full bg-card p-1 shadow-sm">
              {iconUrl ? (
                <img src={iconUrl} alt="" className="h-4 w-4 rounded-full object-cover" />
              ) : iconEmoji ? (
                <span className="flex h-4 w-4 items-center justify-center text-xs">{iconEmoji}</span>
              ) : (
                <div
                  className="h-4 w-4 rounded-full"
                  style={{
                    background: accentColor || "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.6))",
                  }}
                />
              )}
            </div>
          </div>
        </div>

        <div className="min-h-[72px] px-4 py-4 pt-5">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-sm font-medium tracking-wide text-foreground transition-colors group-hover:text-primary">
              {title}
            </span>

            {showProgress ? (
              <div className="mt-1 flex items-center gap-2">
                <Progress value={progressPct} className="h-1.5 flex-1" />
                <span className="whitespace-nowrap text-xs text-muted-foreground">{progressPct}%</span>
              </div>
            ) : (
              <span className="truncate text-xs text-muted-foreground">{author || "Shattara AI"}</span>
            )}
          </div>
        </div>
        </div>
      </a>
    </div>
  );
};

export const CourseCardSkeleton: React.FC = () => (
  <div className="h-full overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
    <Skeleton className="h-14 rounded-none" />
    <div className="min-h-[72px] px-4 py-4 pt-5">
      <div className="space-y-2">
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  </div>
);